import { useAuth0 } from "@auth0/auth0-react";
import { Capacitor } from "@capacitor/core";
import { useEffect, useRef } from "react";
import { useUserData } from "../contexts/user-data-context";
import { api } from "../lib/api";
import * as RevenueCatService from "../services/revenueCatService";

/**
 * Component that fetches user profile data and populates the UserDataContext
 * This runs once when the user is authenticated
 */
export function UserDataLoader() {
	const { isAuthenticated, isLoading, user } = useAuth0();
	const { userData, updateUserData } = useUserData();
	const hasFetched = useRef(false);

	useEffect(() => {
		// Only fetch if authenticated, not loading, and haven't fetched yet
		if (!isAuthenticated || isLoading || hasFetched.current || userData) {
			return;
		}

		// Mark as fetched immediately to prevent retries on error
		hasFetched.current = true;

		const fetchUserData = async () => {
			try {
				const response = await api.get("/api/user");
				const profileData = response.data.user;
				const userDetails = response.data.details;

				// Update the context with the fetched data
				updateUserData({
					id: profileData.id,
					email: profileData.email,
					displayName: profileData.displayName || user?.name || "User",
					userImage: profileData.userImage || user?.picture || null,
					details: userDetails || null,
				});

				// Configure/Link RevenueCat user with database user ID
				const platform = Capacitor.getPlatform();
				try {
					if (platform === "web") {
						await RevenueCatService.configure(profileData.id);
					} else {
						await RevenueCatService.setUserId(profileData.id);
					}
				} catch (_error) {
					console.warn(
						"Failed to configure RevenueCat - continuing without subscription features",
					);
				}
			} catch (error) {
				console.error("Error loading user data:", error);
				// Don't retry on error - hasFetched stays true to prevent loops
			}
		};

		fetchUserData();
	}, [isAuthenticated, isLoading, userData, updateUserData, user]);

	return null; // This component doesn't render anything
}
